'use client';

import {
  PERSISTED_VARIANT_OPTIONS_KEY,
  useLocalStore,
} from '@/contexts/LocalStore';
import {
  applyPersistedOptions,
  areOptionsValid,
  normalizeVariantOptionValue,
  optionNamesMatch,
  spreadOptionsObj,
  useUpdateURL,
} from '@/utils/product/product-context-helpers';
import useProductState from '@/utils/product/use-product-state';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  startTransition,
} from 'react';

const ProductContext = createContext(undefined);

// ─── provider ────────────────────────────────────────────────────────────────

export function ProductProvider({
  children,
  productData,
  defaultVariantId,
  additionalProducts: initialAdditionalProducts = [],
}) {
  const shopifyProduct = productData?.shopifyProduct;
  const updateURL = useUpdateURL();
  const { getItem, setItem } = useLocalStore();
  const [additionalProducts, setAdditionalProducts] = useState(
    initialAdditionalProducts,
  );
  const hasAppliedPersistedOptions = useRef(false);
  const [hasResolvedPersistedOptions, setHasResolvedPersistedOptions] = useState(false);

  const { mergeOptionsIntoState, selectedVariant, selectedVariantPrices } =
    useProductState(productData, defaultVariantId);

  // Reset during render so ElevarProduct never sees a stale resolved=true.
  const productKey = shopifyProduct?.id ?? productData?.shopify_product_id;
  const [lastProductKey, setLastProductKey] = useState(productKey);
  if (productKey !== lastProductKey) {
    setLastProductKey(productKey);
    setHasResolvedPersistedOptions(false);
  }

  useEffect(() => {
    hasAppliedPersistedOptions.current = false;
  }, [productKey]);

  /**
   * @param {Array<{ name: string, value: string }>} options
   * @param {{ persist?: boolean }} [opts] - When true, also saves to
   *   PERSISTED_VARIANT_OPTIONS_KEY, read elsewhere for cross-page hints (cards).
   */
  const updateOptionsAndURL = useCallback(
    (options, { persist = false } = {}) => {
      // Case-insensitive name; exact value must exist on Shopify option definitions.
      if (!areOptionsValid(shopifyProduct, options)) {
        console.error('Invalid options:', options);
        return;
      }
      mergeOptionsIntoState(options);
      updateURL(spreadOptionsObj(options));

      if (persist) {
        const persistedOptions = getItem(PERSISTED_VARIANT_OPTIONS_KEY) ?? {};

        const nextPersistedOptions = options.reduce(
          (accumulator, option) => ({
            ...accumulator,
            [option.name.toLowerCase()]: normalizeVariantOptionValue(
              option.name,
              option.value,
            ),
          }),
          persistedOptions,
        );

        setItem(PERSISTED_VARIANT_OPTIONS_KEY, nextPersistedOptions);
      }
    },
    [getItem, mergeOptionsIntoState, setItem, shopifyProduct, updateURL],
  );

  // Once per product: merge persisted hints into state/URL (skipping OOS combinations
  // and keys already in the query string, URL wins). `hasResolvedPersistedOptions`
  // flips true once this decision lands — whether or not a variant was actually
  // applied — so consumers (e.g. ElevarProduct) can wait for the post-hydration
  // variant instead of firing against the page's pre-hydration default.
  useEffect(() => {
    if (hasAppliedPersistedOptions.current) {
      return;
    }
    if (
      !shopifyProduct?.options?.length ||
      !selectedVariant?.selectedOptions?.length
    ) {
      hasAppliedPersistedOptions.current = true;
      startTransition(() => {
        setHasResolvedPersistedOptions(true);
      });
      return;
    }
    hasAppliedPersistedOptions.current = true;

    const persistedOptions = getItem(PERSISTED_VARIANT_OPTIONS_KEY) ?? {};
    const searchParams = new URLSearchParams(window.location.search);
    const variantToApply = applyPersistedOptions(
      shopifyProduct,
      selectedVariant.selectedOptions,
      persistedOptions,
      new Set(searchParams.keys()),
    );

    if (!variantToApply) {
      startTransition(() => {
        setHasResolvedPersistedOptions(true);
      });
      return;
    }

    startTransition(() => {
      mergeOptionsIntoState(variantToApply.selectedOptions);
      updateURL(spreadOptionsObj(variantToApply.selectedOptions));
      setHasResolvedPersistedOptions(true);
    });
  }, [
    getItem,
    mergeOptionsIntoState,
    selectedVariant,
    shopifyProduct,
    updateURL,
  ]);

  // ── context value ──────────────────────────────────────────────────────────

  const value = useMemo(
    () => ({
      productData,
      selectedVariant,
      selectedVariantPrices,
      updateOptions: updateOptionsAndURL,
      additionalProducts,
      setAdditionalProducts,
      hasResolvedPersistedOptions,
    }),
    [
      productData,
      selectedVariant,
      selectedVariantPrices,
      updateOptionsAndURL,
      additionalProducts,
      setAdditionalProducts,
      hasResolvedPersistedOptions,
    ],
  );

  return (
    <ProductContext.Provider value={value}>
      {children}
    </ProductContext.Provider>
  );
}

// ─── hook ────────────────────────────────────────────────────────────────────

export function useProduct() {
  const context = useContext(ProductContext);
  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider');
  }
  return context;
}

export function useOptionalProduct() {
  return useContext(ProductContext);
}

// ─── utility export ───────────────────────────────────────────────────────────

/**
 * Returns the variant that would be active if `option` were changed to
 * `originalValue`, keeping all other currently-selected options the same.
 */
export function getVariantBasedOnSelectedOptions(
  option,
  originalValue,
  productData,
  selectedVariant,
) {
  if (!selectedVariant || !productData?.shopifyProduct?.variants) return null;

  const candidates = selectedVariant.selectedOptions
    .filter((opt) => !optionNamesMatch(opt.name, option))
    .concat({ name: option, value: originalValue });

  return (
    productData.shopifyProduct.variants.find((variant) =>
      candidates.every((choice) =>
        variant.selectedOptions.some(
          (o) =>
            optionNamesMatch(o.name, choice.name) && o.value === choice.value,
        ),
      ),
    ) ?? null
  );
}
